import { Box, ClickAwayListener, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import { OgAssistCalloutButton } from '@opengov/agents-browser-sdk';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IEditorMessages, IUpdateStoryContentProps } from '../../../../../../../types';
import {
  MEDIA_BACKGROUND,
  MEDIA_EMBED,
  MEDIA_IMAGE,
  MEDIA_TABLE_TILE,
  MEDIA_TILE,
  MEDIA_VIDEO,
  CUSTOM_SPREADSHEET_REPORT_MEDIA_TYPE
} from '../../../../../../constants/modals';
import { useMarkSummaryRefreshPending } from '../../../../../../utils/ogAssistSummaryRefresh';
import { styles } from './MediaOptionsGearButton.styles';
import { AiEnabledMediaType, HtmlExtractionResult } from './htmlParsing/htmlParsingUtils';
import { AiGenerateSummaryModal } from '../../../../../../../modals/components/AiGenerateSummaryModal/AiGenerateSummaryModal';
import { EditorValueSource } from '../../tinyMCEStoryEditorUtils';
import { useModal } from '../../../../../StoryView/StoryView/contexts/ModalContext';
import { EditCustomSpreadsheetMediaModalContainer } from '../../../../../Publications/CustomReports/EditCustomSpreadsheetMediaModal/EditCustomSpreadsheetMediaModalContainer';
import { IStory, IStoryContent } from '@opengov/stories-shared/types';

export type MediaType =
  | typeof MEDIA_TILE
  | typeof MEDIA_TABLE_TILE
  | typeof MEDIA_IMAGE
  | typeof MEDIA_VIDEO
  | typeof MEDIA_EMBED
  | typeof MEDIA_BACKGROUND
  | typeof CUSTOM_SPREADSHEET_REPORT_MEDIA_TYPE;

type Props = {
  publicationId: string | undefined;
  storyContent: IStoryContent;
  story: IStory;
  isTemplate: boolean;
  entityId: number;
  entitySubdomain: string;
  mediaType: MediaType;
  mediaIndex: number;
  hasNonHeaderEditable: boolean;
  onGenerateSummary: (
    aiEnabledMediaType: MediaType,
    contentLength: string
  ) => Promise<{ summary: string; extraction: HtmlExtractionResult }>;
  afterGenerateSummary: (extraction: HtmlExtractionResult, summary: string) => void;
  /**
   * Resolves the identifiers for the media this gear button is attached to, read from the rendered
   * DOM. Called when the menu opens rather than on every render, and returns null when the media
   * cannot be identified (in which case the OG Assist entry is withheld).
   */
  getAiSummaryContext: () => HtmlExtractionResult | null;
  legacyHandleEditMedia: () => void;
  updateStoryContent: (storyContent: IStoryContent) => void;
  updateStoryContentDB: (props: IUpdateStoryContentProps) => void;
  messages: IEditorMessages;
  canUseAiToGenerateContent: boolean;
  canUseStoriesSummarizerGA: boolean;
  /** Routes "Generate Summary" to OG Assist instead of the existing summary modal. */
  canUseOgAssistGenerateSummary: boolean;
  isAiEnabledByAdmin: boolean;
  editorValueSource: EditorValueSource;
};

export function MediaOptionsGearButton({
  publicationId,
  storyContent,
  story,
  isTemplate,
  entityId,
  entitySubdomain,
  mediaType,
  mediaIndex,
  hasNonHeaderEditable,
  onGenerateSummary,
  afterGenerateSummary,
  getAiSummaryContext,
  legacyHandleEditMedia,
  updateStoryContent,
  updateStoryContentDB,
  messages,
  canUseAiToGenerateContent,
  canUseStoriesSummarizerGA,
  canUseOgAssistGenerateSummary,
  isAiEnabledByAdmin,
  editorValueSource
}: Props) {
  const { setCurrentModal } = useModal();
  const markSummaryRefreshPending = useMarkSummaryRefreshPending();
  // Must match the value the NavBar mounts OG Assist with, or the callout has no shell to drive.
  const agentsBaseUrl = window.process.env.AGENTS_BASE_URL ?? '';
  // This also narrows the mediaType type
  const isAiEnabledMediaType = mediaType === MEDIA_TILE || mediaType === MEDIA_TABLE_TILE || mediaType === MEDIA_IMAGE;
  const [isGenerateSummaryModalVisible, setIsGenerateSummaryModalVisible] = useState(false);
  const [aiLaunch, setAiLaunch] = useState<ReturnType<typeof buildAiSummaryLaunch> | null>(null);
  const [hasCallout, setHasCallout] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuListRef = useRef<HTMLUListElement>(null);
  const calloutRef = useRef<HTMLDivElement>(null);
  const isMenuOpen = Boolean(anchor);
  const isAiEntryAvailable =
    isAiEnabledByAdmin &&
    isAiEnabledMediaType &&
    hasNonHeaderEditable &&
    (canUseAiToGenerateContent || canUseStoriesSummarizerGA);

  /**
   * The callout's button is rendered asynchronously by a CDN bundle, and not at all for a user without the
   * OG Assist entitlement, so the row follows the button's actual presence instead of a snapshot taken when
   * the menu opens.
   */
  useEffect(() => {
    const container = calloutRef.current;
    if (!container) {
      setHasCallout(false);
      return undefined;
    }
    const syncCallout = () => setHasCallout(Boolean(container.querySelector('button')));
    syncCallout();
    const observer = new MutationObserver(syncCallout);
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isAiEntryAvailable, canUseOgAssistGenerateSummary, agentsBaseUrl]);

  /**
   * The callout owns its own button, rendered by a CDN bundle into its own React root, and exposes
   * no imperative launch API. Driving that button is what lets the menu row itself be an ordinary
   * `MenuItem` matching its siblings, instead of a foreign button nested inside one.
   */
  const launchOgAssist = () => {
    // Unidentifiable media would open a conversation with no prompt or context.
    if (aiLaunch === null) return;
    // The agent writes the summary into the saved section; re-read it when the drawer closes.
    markSummaryRefreshPending({ storyId: story._id, storyContentId: storyContent._id, entityId, isTemplate });
    calloutRef.current?.querySelector('button')?.click();
  };

  // when the menu opens, focus the MenuList root so MUI's built-in arrow-key/Home/End/typeahead handling becomes active.
  useEffect(() => {
    if (isMenuOpen) {
      menuListRef.current?.focus({ preventScroll: true });
    }
  }, [isMenuOpen]);

  const onHandleEditMedia = () => {
    switch (mediaType) {
      case MEDIA_TILE:
      case MEDIA_TABLE_TILE:
      case MEDIA_IMAGE:
      case MEDIA_VIDEO:
      case MEDIA_EMBED:
      case MEDIA_BACKGROUND: {
        legacyHandleEditMedia();
        break;
      }

      case CUSTOM_SPREADSHEET_REPORT_MEDIA_TYPE: {
        setCurrentModal(
          <EditCustomSpreadsheetMediaModalContainer
            publicationId={publicationId}
            onClose={() => setCurrentModal(null)}
            storyContent={storyContent}
            story={story}
            entityId={entityId}
            entitySubdomain={entitySubdomain}
            updateStoryContent={updateStoryContent}
            updateStoryContentDB={updateStoryContentDB}
            mediaIndex={mediaIndex}
          />
        );
        break;
      }

      default: {
        const _exhaustiveCheck: never = mediaType;
        console.error('Unhandled media type:', _exhaustiveCheck);
        break;
      }
    }
  };

  if (isAiEntryAvailable) {
    // clicking the gear button toggles the menu open/closed
    const toggleMenu = (target: HTMLElement) => {
      if (anchor) {
        setAnchor(null);
        return;
      }
      if (canUseOgAssistGenerateSummary) {
        // Resolved on open rather than on every render: it reads the rendered DOM. The callout is
        // already mounted by now, so the new prompt reaches it before the row can be clicked.
        const extraction = getAiSummaryContext();
        setAiLaunch(
          extraction ? buildAiSummaryLaunch(extraction, entityId, publicationId, story._id, storyContent._id) : null
        );
      }
      setAnchor(target);
    };

    const handleGearClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      toggleMenu(event.currentTarget);
    };

    const handleGearKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleMenu(event.currentTarget);
      }
    };

    const handleMenuClose = (event?: MouseEvent | TouchEvent) => {
      if (event && anchor?.contains(event.target as Node)) {
        return;
      }
      setAnchor(null);
    };

    // Popper is not a Modal, so Escape-to-close must be wired up explicitly.
    const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        handleMenuClose();
      }
    };

    // close the popper when focus is moved outside the menu
    const handleMenuBlur = (event: React.FocusEvent<HTMLDivElement>) => {
      const nextFocusTarget = event.relatedTarget as Node | null;

      // if focus is moving to the gear button itself, don't close the menu
      // wait for the gear button's click/keydown handler to toggle the menu open/closed
      if (nextFocusTarget && anchor?.contains(nextFocusTarget)) {
        return;
      }
      if (!nextFocusTarget || !event.currentTarget.contains(nextFocusTarget)) {
        handleMenuClose();
      }
    };

    // Without the flag, the row opens the existing summary modal; with it, the row needs a launchable callout.
    const isGenerateSummaryRowVisible = canUseOgAssistGenerateSummary ? hasCallout && aiLaunch !== null : true;

    const handleGenerateSummary = () => {
      if (canUseOgAssistGenerateSummary) {
        launchOgAssist();
      } else {
        setIsGenerateSummaryModalVisible(true);
      }
      handleMenuClose();
    };

    return (
      <>
        {canUseOgAssistGenerateSummary && agentsBaseUrl ? (
          <Box ref={calloutRef} sx={styles.hiddenCallout} aria-hidden>
            <OgAssistCalloutButton
              baseURL={agentsBaseUrl}
              label={messages.generateSummary}
              prompt={aiLaunch?.prompt ?? ''}
              context={aiLaunch?.context ?? []}
            />
          </Box>
        ) : null}
        <a
          role="button"
          data-test="edit-media-button"
          aria-label={messages.update}
          tabIndex={0}
          onClick={handleGearClick}
          onKeyDown={handleGearKeyDown}
        >
          <span className="icon-settings-1" contentEditable={false} />
        </a>
        <Popper
          id="story-popover"
          open={isMenuOpen}
          anchorEl={anchor}
          placement="bottom-end"
          disablePortal
          // flip and preventOverflow swaps the popper to the opposite side, we don't want that.
          modifiers={[
            { name: 'offset', options: { offset: [0, 4] } },
            { name: 'flip', enabled: false },
            { name: 'preventOverflow', enabled: false }
          ]}
        >
          <ClickAwayListener onClickAway={handleMenuClose}>
            <Paper sx={styles.embedDropdown} onBlur={handleMenuBlur}>
              <MenuList ref={menuListRef} onKeyDown={handleMenuKeyDown}>
                <MenuItem
                  onClick={() => {
                    onHandleEditMedia();
                    handleMenuClose();
                  }}
                >
                  {getUpdateMenuItemTitle(mediaType, messages)}
                </MenuItem>
                {isGenerateSummaryRowVisible ? (
                  <MenuItem onClick={handleGenerateSummary}>{` ✨ ${messages.generateSummary}`}</MenuItem>
                ) : null}
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>
        {isGenerateSummaryModalVisible &&
          createPortal(
            <AiGenerateSummaryModal
              onGenerateSummary={onGenerateSummary}
              afterGenerateSummary={afterGenerateSummary}
              mediaType={mediaType}
              closeModal={() => setIsGenerateSummaryModalVisible(false)}
              messages={messages.generateSummaryModal}
              editorValueSource={editorValueSource}
            />,
            document.getElementById('app') as Element
          )}
      </>
    );
  }

  return (
    <a role="button" aria-label={messages.update} data-test="edit-media-button" onClick={onHandleEditMedia}>
      <span
        className="icon-settings-1"
        data-hint={messages.update}
        data-hint-position="bottom"
        data-hint-align="center"
        contentEditable={false}
      />
    </a>
  );
}

/**
 * Seeds a new OG Assist conversation for the media the gear button is attached to. The callout
 * auto-sends this as the first user message, so it is phrased as a request rather than a heading.
 *
 * The identifiers travel in `context` rather than interpolated into the prompt, so the agent reads
 * them as structured values and the wording stays stable if the shape changes.
 */
function buildAiSummaryLaunch(
  extraction: HtmlExtractionResult,
  entityId: number,
  publicationId: string | undefined,
  storyId: string,
  storyContentId: string
): { prompt: string; context: Array<{ key: string; label: string; value: string }> } {
  const context = [
    { key: 'entity_id', label: 'Entity', value: String(entityId) },
    { key: 'media_type', label: 'Media type', value: extraction.mediaType },
    { key: 'story_id', label: 'Story', value: storyId },
    // The saved section the summary is written into; the tile (embed_id / image_src) tells the agent which block.
    { key: 'story_content_id', label: 'Story section', value: storyContentId },
    ...(publicationId === undefined ? [] : [{ key: 'publication_id', label: 'Publication', value: publicationId }]),
    ...('embedId' in extraction
      ? [{ key: 'embed_id', label: 'Embed', value: extraction.embedId }]
      : [{ key: 'image_src', label: 'Image', value: extraction.imageSrc }])
  ];

  const prompt =
    'embedId' in extraction
      ? 'Summarize the data shown in this story tile, and give me a summary I can add to the story.'
      : 'Summarize the image in this story, and give me a summary I can add to the story.';

  return { prompt, context };
}

function getUpdateMenuItemTitle(mediaType: AiEnabledMediaType, messages: IEditorMessages): string {
  switch (mediaType) {
    case MEDIA_TABLE_TILE:
      return `🔧 ${messages.updateReportTable}`;

    case MEDIA_TILE:
      return `🔧 ${messages.updateTile}`;

    default:
      return `🔧 ${messages.updateMedia}`;
  }
}
